import classNames from 'classnames';
import type { ReactNode } from 'react';

import Icon from '../Icon';
import type { IconClassName } from '../Icon';

export type BadgeColor =
  | 'blue'
  | 'cyan'
  | 'gray'
  | 'dark-gray'
  | 'darker-gray'
  | 'dark-green'
  | 'dark-red'
  | 'dark-yellow'
  | 'medium-yellow'
  | 'green'
  | 'light-blue'
  | 'light-gray'
  | 'lighter-gray'
  | 'light-green'
  | 'light-purple'
  | 'light-red'
  | 'light-yellow'
  | 'orange'
  | 'pink'
  | 'purple'
  | 'red'
  | 'wine-red'
  | 'yellow'
  | 'white';

export type VulnBadgeColor =
  | 'critical-severity-vuln'
  | 'high-severity-vuln'
  | 'medium-severity-vuln'
  | 'low-severity-vuln'
  | 'unknown-severity-vuln';

export type AlertColor =
  | 'alert-info'
  | 'alert-warning'
  | 'alert-error';

export type BadgeShape = 'box' | 'circle';

export type BadgeSize = 'micro' | 'small' | 'medium' | 'large';

/** Runtime arrays for Storybook iteration */
export const BADGE_COLORS: BadgeColor[] = [
  'blue', 'cyan', 'gray', 'dark-gray', 'darker-gray', 'dark-green', 'dark-red',
  'dark-yellow', 'medium-yellow', 'green', 'light-blue', 'light-gray', 'lighter-gray',
  'light-green', 'light-purple', 'light-red', 'light-yellow', 'orange', 'pink',
  'purple', 'red', 'wine-red', 'yellow', 'white',
];

export const VULN_BADGE_COLORS: VulnBadgeColor[] = [
  'critical-severity-vuln', 'high-severity-vuln', 'medium-severity-vuln',
  'low-severity-vuln', 'unknown-severity-vuln',
];

export const ALERT_COLORS: AlertColor[] = [
  'alert-info', 'alert-warning', 'alert-error',
];

export const BADGE_SHAPES: BadgeShape[] = ['box', 'circle'];

export const BADGE_SIZES: BadgeSize[] = ['micro', 'small', 'medium', 'large'];

export type BadgeProps = {
  /**
   * Background color of the badge
   */
  color: BadgeColor | AlertColor | VulnBadgeColor;
  /**
   * Optional icon to prepend the text in the badge
   */
  icon?: IconClassName;
  /**
   * The size of the badge, roughly matches button sizing @default 'micro'
   */
  size?: BadgeSize;
  /**
   * The shape of the badge @default 'box'
   */
  shape?: BadgeShape;
  /**
   * Optional outline style.
   * Background becomes transparent and the provided color becomes foreground.
   */
  outline?: boolean;
  /**
   * Optional invert style - colors are inverted.
   * Light colors do not have inverts
   */
  invert?: boolean;
  /**
   * Fill the parent container.
   */
  fullWidth?: boolean;
  /**
   * Content to be displayed in the badge
   */
  children?: ReactNode;
  /**
   * Optional title attribute for the badge. Useful when you have are displaying a count and are truncating the number.
   */
  title?: string;
};

/**
 * Generic badge with background color driven by @param color. The shape of the badge is by default a box shape, however can be made circular by explicitly passing in @param shape
 */
const Badge = ({
  color,
  icon,
  children,
  outline,
  invert,
  fullWidth,
  size = 'micro',
  shape = 'box',
  title,
}: BadgeProps) => (
  <div
    className={classNames(
      'color-badge',
      color,
      `color-badge__shape-${shape}`,
      {
        'color-badge__outline': outline,
        'color-badge__invert': invert || outline,
        'color-badge__size-micro': size === 'micro',
        'color-badge__size-small': size === 'small',
        'color-badge__size-medium': size === 'medium',
        'color-badge__size-large': size === 'large',
        'width-full': fullWidth,
      },
    )}
    title={title}
  >
    {icon && <div className="color-badge__icon"><Icon icon={icon} /></div>}
    {fullWidth ? <span className="width-full">{children}</span> : children}
  </div>
);

export default Badge;
